import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, BadgeCheck, Flame, BookOpen, Plus } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useBook } from '../context/BookContext';
import { useAuth } from '../context/AuthContext';

// Fire rating display component
const FireRating = ({ rating, size = 'sm' }) => {
  const numFlames = Math.round(rating || 0);
  const sizeClass = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Flame
          key={i}
          className={`${sizeClass} ${i <= numFlames ? 'text-orange-500 fill-orange-500' : 'text-muted-foreground/30'}`}
        />
      ))}
    </div>
  );
};

const TalentCard = ({ talent, size = 'default', showAddToBook = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToBook, isInBook } = useBook();

  const defaultImage = 'https://images.pexels.com/photos/1763069/pexels-photo-1763069.jpeg';
  const image = talent.portfolio_images?.[0] || talent.picture || defaultImage;

  const sizeClasses = {
    default: 'col-span-12 md:col-span-6 lg:col-span-3',
    large: 'col-span-12 md:col-span-8 lg:col-span-6',
    small: 'col-span-6 md:col-span-4 lg:col-span-2'
  };

  return (
    <div
      className={`talent-card cursor-pointer group ${sizeClasses[size]}`}
      onClick={() => navigate(`/talent/${talent.user_id}`)}
      data-testid={`talent-card-${talent.user_id}`}
    >
      <div className="talent-image-container aspect-[3/4] relative">
        <img
          src={image}
          alt={talent.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="card-overlay absolute inset-0" />
        
        {/* Verified Badge */}
        {talent.is_verified && (
          <div className="absolute top-3 right-3">
            <div className="verified-badge px-2 py-1 rounded-full flex items-center gap-1 text-xs">
              <BadgeCheck className="h-3 w-3" />
              Verified
            </div>
          </div>
        )}

        {/* Add to Book Button - For Bookers */}
        {showAddToBook && user?.user_type === 'client' && (
          <Button
            variant="secondary"
            size="sm"
            className={`absolute top-3 left-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${
              isInBook(talent.user_id) ? 'bg-amber-500 text-white hover:bg-amber-600' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isInBook(talent.user_id)) {
                addToBook(talent);
              }
            }}
            data-testid={`add-to-book-${talent.user_id}`}
          >
            {isInBook(talent.user_id) ? (
              <>
                <BookOpen className="h-3 w-3 mr-1" />
                In Book
              </>
            ) : (
              <>
                <Plus className="h-3 w-3 mr-1" />
                Add
              </>
            )}
          </Button>
        )}

        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 mb-2">
            {talent.categories?.slice(0, 2).map((cat, idx) => (
              <Badge key={idx} variant="secondary" className="category-badge text-[10px]">
                {cat}
              </Badge>
            ))}
          </div>
          
          <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
            {talent.screen_name || talent.name}
          </h3>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-white/70">
              {talent.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {talent.location}
                </span>
              )}
            </div>
            
            {/* Fire Rating with stats */}
            <div className="flex items-center gap-1.5">
              <Flame className="h-4 w-4 text-orange-500 fill-orange-500" />
              <span className="text-white font-medium">{talent.rating?.toFixed(1) || '0.0'}</span>
              <span className="text-white/50 text-xs">
                ({talent.review_count || 0}) · {talent.completed_bookings || 0} jobs
              </span>
            </div>
          </div>

          {talent.hourly_rate && (
            <p className="mt-2 price-tag text-lg">
              ${talent.hourly_rate}/hr
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export { FireRating };
export default TalentCard;
